import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Static narrative — direct port of the "three rates" + worked example block
// in sync_health_report.py. Wrapped in an Accordion so the dashboard isn't
// dominated by paragraph text on first load.
const DefinitionsSection = () => (
    <Accordion defaultExpanded sx={{ borderRadius: 2, '&:before': { display: 'none' } }} elevation={0}
        className="sh-card" style={{ padding: 0 }}>
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 3, py: 1.25, '& .MuiAccordionSummary-content': { my: 1 } }}
        >
            <Typography className="sync-health-section-title">The three rates — what they mean</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
            <Box className="sh-defs-block" sx={{ background: 'transparent', border: 'none', p: 0 }}>
                <Box className="sh-metric-def">
                    <Box>
                        <span className="name">Provider-run rate</span> &nbsp;·&nbsp; <i>integration reliability</i>
                    </Box>
                    <Box>
                        A <b>provider run</b> is all jobs for one (client, provider) pair on one night. It succeeds iff every
                        job in it succeeded. This rate asks: <b>of every integration-night we tried, what fraction worked?</b>
                    </Box>
                    <Box className="formula">rate = successful runs / total runs</Box>
                </Box>
                <Box className="sh-metric-def orange">
                    <Box>
                        <span className="name">Client-night clean rate</span> &nbsp;·&nbsp; <i>stakeholder experience</i>
                    </Box>
                    <Box>
                        A <b>client night</b> is one (client, night) pair. It's <i>clean</i> iff every one of that client's
                        provider runs that night succeeded. One failed provider = a dirty night for that client.
                    </Box>
                    <Box className="formula">rate = clean client-nights / total client-nights</Box>
                </Box>
                <Box className="sh-metric-def red">
                    <Box>
                        <span className="name">System-night clean rate</span> &nbsp;·&nbsp; <i>whole-system health</i>
                    </Box>
                    <Box>
                        A <b>system night</b> is one night across the whole platform. It's clean iff zero provider runs failed
                        anywhere. This rate asks: <b>what fraction of nights were completely spotless?</b>
                    </Box>
                    <Box className="formula">rate = fully-clean nights / total nights</Box>
                </Box>

                <Typography sx={{ mt: 2, mb: 1, fontWeight: 700, color: '#264653', fontSize: 14 }}>
                    Worked example — 5 clients × 5 providers = 25 runs per night
                </Typography>
                <table className="sh-table" style={{ fontSize: 13 }}>
                    <thead>
                        <tr>
                            <th style={{ width: '45%' }}>Tonight's outcome</th>
                            <th className="num">Provider-run</th>
                            <th className="num">Client-night</th>
                            <th className="num">System-night</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>All 25 runs succeed.</td>
                            <td className="num sh-good">100%</td>
                            <td className="num sh-good">100%</td>
                            <td className="num sh-good">100%</td>
                        </tr>
                        <tr>
                            <td>1 run fails anywhere.</td>
                            <td className="num">96% <span style={{ color: '#888' }}>(24/25)</span></td>
                            <td className="num">80% <span style={{ color: '#888' }}>(4 of 5 clients clean)</span></td>
                            <td className="num sh-bad">0%</td>
                        </tr>
                        <tr>
                            <td><b>4 of Baldwin's 5 providers fail</b>.</td>
                            <td className="num">84% <span style={{ color: '#888' }}>(21/25)</span></td>
                            <td className="num">80% <span style={{ color: '#888' }}>(Baldwin alone is dirty)</span></td>
                            <td className="num sh-bad">0%</td>
                        </tr>
                        <tr>
                            <td>All 5 of Baldwin's providers fail.</td>
                            <td className="num">80% <span style={{ color: '#888' }}>(20/25)</span></td>
                            <td className="num">80% <span style={{ color: '#888' }}>(same — 4 of 5 clients clean)</span></td>
                            <td className="num sh-bad">0%</td>
                        </tr>
                        <tr>
                            <td>Powerschool fails for 3 different clients.</td>
                            <td className="num">88% <span style={{ color: '#888' }}>(22/25)</span></td>
                            <td className="num">40% <span style={{ color: '#888' }}>(only 2 of 5 clients clean)</span></td>
                            <td className="num sh-bad">0%</td>
                        </tr>
                        <tr>
                            <td>2 different clients each lose 1 provider.</td>
                            <td className="num">92% <span style={{ color: '#888' }}>(23/25)</span></td>
                            <td className="num">60% <span style={{ color: '#888' }}>(3 of 5 clients clean)</span></td>
                            <td className="num sh-bad">0%</td>
                        </tr>
                    </tbody>
                </table>
                <Typography sx={{ fontSize: 12, color: '#666', mt: 1 }}>
                    Rows 2, 3, 4 all show client-night at 80% — whether one provider or five providers fail, it's still the
                    same client having a dirty night. Provider-run distinguishes scale (96% → 84% → 80%). Rows 2 and 5 have
                    similar provider-run (96% vs 88%) but very different client-night (80% vs 40%) because the failures
                    spread across more clients.
                </Typography>
            </Box>
        </AccordionDetails>
    </Accordion>
);

export default DefinitionsSection;
